import { z } from 'zod'
import { PrescreenDecisionSchema, PrescreenIndividualIdSchema } from './prescreen'
import { PhoneSchema } from './phone'
import { AuditPropsSchema, EmptyStringSchema } from './common'

export const REQUIRABLE_RESIDENT_FIELDS: Exclude<keyof TinyResidentData, 'decision' | 'individualId'>[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
]
export const RequirableResidentFieldSchema = z.enum(REQUIRABLE_RESIDENT_FIELDS)
export type RequirableResidentField = z.infer<typeof RequirableResidentFieldSchema>

export const DEFAULT_REQUIRED_RESIDENT_FIELDS: RequirableResidentField[] = ['firstName', 'lastName']

export const FieldDataSchema = z.object({
  field: z.string(),
  value: z.string(),
})
export type FieldData = z.infer<typeof FieldDataSchema>

export const CreditScoreSchema = z.object({
  gte: z.number(),
  lte: z.number(),
})
export type CreditScore = z.infer<typeof CreditScoreSchema>

const UnsavedResidentDataSchema = z.object({
  name: z.string(),
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  suffix: z.string().optional(),
  prefix: z.string().optional(),
  type: z.enum(['Owner', 'Renter', 'Dependent']).optional(),
  gender: z.enum(['M', 'F', 'O']).optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  favorite: z.boolean().optional(),
  checked: z.boolean().optional(),
  fields: z.array(FieldDataSchema).optional(),
  age: z.number().min(0).optional(),
  creditScore: CreditScoreSchema.optional(),
  hasAge: z.boolean().optional(),
  hasCreditScore: z.boolean().optional(),
})
export type UnsavedResidentData = z.infer<typeof UnsavedResidentDataSchema>

export const ResidentDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...UnsavedResidentDataSchema.shape,
  decision: PrescreenDecisionSchema.optional().meta({ audience: 'none' }),
  individualId: PrescreenIndividualIdSchema.optional().meta({ audience: 'none' }),
})
export type ResidentData = z.infer<typeof ResidentDataSchema>

export const TinyResidentSchema = z
  .object({
    name: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.optional(z.string().email().or(EmptyStringSchema)),
    phone: z.optional(PhoneSchema.or(EmptyStringSchema)),
    phone2: z.optional(PhoneSchema.or(EmptyStringSchema)),
    businessName: z.string().optional(),
    ...ResidentDataSchema.pick({ decision: true, individualId: true }).shape,
    ...ResidentDataSchema.pick({ age: true, creditScore: true, hasAge: true, hasCreditScore: true }).shape,
  })
  .meta({
    id: 'tinyResident',
    title: 'Tiny Resident',
    description: 'Minimal resident information',
  })
export type TinyResidentData = z.infer<typeof TinyResidentSchema>

export const UpsertTinyResidentSchema = TinyResidentSchema.omit({ phone: true, phone2: true }).extend({
  phone: z.string().optional(),
  phone2: z.string().optional(),
})
export type UpsertTinyResidentData = z.infer<typeof UpsertTinyResidentSchema>

export function residentToTinyResident(resident?: ResidentData): TinyResidentData | undefined {
  if (!resident) return
  return {
    firstName: resident.firstName || resident.name,
    lastName: resident.lastName,
    email: resident.email,
    phone: resident.phone,
  }
}
